import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  Pagination,
  Tooltip,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { auditLogAPI, adminUsersAPI } from '../services/api'

// Action indicator styles
const actionStyles = {
  create: { color: '#16a34a', label: 'Create' },
  update: { color: '#0284c7', label: 'Update' },
  delete: { color: '#dc2626', label: 'Delete' },
  reorder: { color: '#ca8a04', label: 'Reorder' },
}

function ActionIndicator({ action }) {
  const style = actionStyles[action] || { color: '#71717a', label: action }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem', textTransform: 'capitalize' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function AuditLog() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedLog, setSelectedLog] = useState(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [adminUsers, setAdminUsers] = useState([])

  // Filter options from API
  const [entityTypes, setEntityTypes] = useState([])
  const [actionTypes, setActionTypes] = useState([])

  // Active filters
  const [entityFilter, setEntityFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const limit = 25

  useEffect(() => {
    fetchFilterOptions()
    fetchAdminUsers()
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [page, entityFilter, actionFilter, userFilter, startDate, endDate])

  const fetchFilterOptions = async () => {
    try {
      const options = await auditLogAPI.getFilterOptions()
      setEntityTypes(options.entityTypes || [])
      setActionTypes(options.actions || [])
    } catch (err) {
      console.error('Error fetching filter options:', err)
    }
  }

  const fetchAdminUsers = async () => {
    try {
      const users = await adminUsersAPI.getAll()
      setAdminUsers(users)
    } catch (err) {
      console.error('Error fetching admin users:', err)
    }
  }

  const fetchLogs = async () => {
    try {
      setLoading(true)
      setError(null)

      const filters = {
        limit,
        offset: (page - 1) * limit,
      }

      if (entityFilter) filters.entity_type = entityFilter
      if (actionFilter) filters.action = actionFilter
      if (userFilter) filters.admin_user_id = userFilter
      if (startDate) filters.start_date = startDate.toISOString()
      if (endDate) filters.end_date = endDate.endOf('day').toISOString()

      const data = await auditLogAPI.getAll(filters)
      setLogs(data.entries || [])
      setTotal(data.total || 0)
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setError(err.response?.data?.error || 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setEntityFilter('')
    setActionFilter('')
    setUserFilter('')
    setStartDate(null)
    setEndDate(null)
    setPage(1)
  }

  const handleExport = async () => {
    try {
      // Fetch all logs with current filters (no pagination)
      const filters = {}
      if (entityFilter) filters.entity_type = entityFilter
      if (actionFilter) filters.action = actionFilter
      if (userFilter) filters.admin_user_id = userFilter
      if (startDate) filters.start_date = startDate.toISOString()
      if (endDate) filters.end_date = endDate.endOf('day').toISOString()
      filters.limit = 10000 // Get all

      const data = await auditLogAPI.getAll(filters)
      const entries = data.entries || []

      const csv = [
        ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'Entity Name', 'IP Address'],
        ...entries.map((log) => [
          log.created_at,
          log.admin_user_name || 'Unknown',
          log.action,
          log.entity_type,
          log.entity_id || '',
          log.entity_name || '',
          log.ip_address || '',
        ]),
      ]
        .map((row) => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-log-${dayjs().format('YYYY-MM-DD')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error exporting:', err)
      setError('Failed to export audit log')
    }
  }

  const formatEntityType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const renderChanges = (log) => {
    if (!log) return null

    const hasChanges = log.changes && Object.keys(log.changes).length > 0
    const hasPrevious = log.previous_values && Object.keys(log.previous_values).length > 0
    const hasNew = log.new_values && Object.keys(log.new_values).length > 0

    if (hasChanges) {
      return Object.entries(log.changes).map(([field, change]) => {
        if (!change || typeof change !== 'object') return null
        return (
          <Box key={field} mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              {field}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'error.50' }} variant="outlined">
                <Typography variant="caption" color="error.main" display="block" gutterBottom>
                  Before
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {change.from !== undefined ? JSON.stringify(change.from, null, 2) : '(empty)'}
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.50' }} variant="outlined">
                <Typography variant="caption" color="success.main" display="block" gutterBottom>
                  After
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {change.to !== undefined ? JSON.stringify(change.to, null, 2) : '(empty)'}
                </Typography>
              </Paper>
            </Stack>
          </Box>
        )
      })
    }

    // Fallback to showing full previous/new values
    if (hasPrevious || hasNew) {
      return (
        <Stack direction="row" spacing={2}>
          {hasPrevious && (
            <Paper sx={{ p: 2, flex: 1, bgcolor: 'grey.50' }} variant="outlined">
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Previous Values
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem', m: 0 }}
              >
                {JSON.stringify(log.previous_values, null, 2)}
              </Typography>
            </Paper>
          )}
          {hasNew && (
            <Paper sx={{ p: 2, flex: 1, bgcolor: 'grey.50' }} variant="outlined">
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                New Values
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem', m: 0 }}
              >
                {JSON.stringify(log.new_values, null, 2)}
              </Typography>
            </Paper>
          )}
        </Stack>
      )
    }

    return (
      <Typography color="text.secondary">No change details available</Typography>
    )
  }

  const hasActiveFilters = entityFilter || actionFilter || userFilter || startDate || endDate
  const totalPages = Math.ceil(total / limit)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5">Audit Log</Typography>
            <Typography variant="body2" color="text.secondary">
              Track all configuration changes made to the intervention system.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchLogs} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
              Export CSV
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 1.5, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityFilter}
                  label="Entity Type"
                  onChange={(e) => { setEntityFilter(e.target.value); setPage(1) }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {entityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatEntityType(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={actionFilter}
                  label="Action"
                  onChange={(e) => { setActionFilter(e.target.value); setPage(1) }}
                >
                  <MenuItem value="">All Actions</MenuItem>
                  {actionTypes.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action.charAt(0).toUpperCase() + action.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>User</InputLabel>
                <Select
                  value={userFilter}
                  label="User"
                  onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {adminUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(val) => { setStartDate(val); setPage(1) }}
                slotProps={{ textField: { size: 'small' } }}
              />

              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(val) => { setEndDate(val); setPage(1) }}
                slotProps={{ textField: { size: 'small' } }}
              />

              {hasActiveFilters && (
                <Button size="small" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </Stack>
        </Paper>

        {/* Logs Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" py={4}>
                        No audit logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(log.created_at).format('MMM D, YYYY')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(log.created_at).format('h:mm A')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.admin_user_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.admin_user_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ActionIndicator action={log.action} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.entity_name || log.entity_id || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatEntityType(log.entity_type)}
                          {log.entity_id && ` (${log.entity_id})`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedLog(log)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total} entries
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}

        {/* Change Details Dialog */}
        <Dialog
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Change Details
            {selectedLog && (
              <Typography variant="body2" color="text.secondary">
                {selectedLog.entity_name || selectedLog.entity_type} - {' '}
                {dayjs(selectedLog.created_at).format('MMM D, YYYY h:mm A')} by{' '}
                {selectedLog.admin_user_name || 'Unknown'}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {selectedLog && (
              <Box>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <ActionIndicator action={selectedLog.action} />
                  <Typography variant="body2">
                    {formatEntityType(selectedLog.entity_type)}
                    {selectedLog.entity_id && ` - ${selectedLog.entity_id}`}
                  </Typography>
                </Stack>
                {selectedLog.ip_address && (
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    IP: {selectedLog.ip_address}
                  </Typography>
                )}
                {renderChanges(selectedLog)}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedLog(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default AuditLog
